import React from 'react';
import { useDispatch } from 'react-redux';
import _ from 'lodash';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Card from '@amzn/meridian/card';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { BoxProps } from '@amzn/meridian/box/box';
import { useAppSelector } from 'src/store/store';
import { deleteRestrictionListItem } from 'src/store/newJmrPageSlice';
import {
  isWeightRestrictionParentTask,
  isYesNoRestrictionTask,
  isTimeRestrictionTask,
} from 'src/utils/restrictionUtils';
import IdentifyBodyLocationField from './selectors/identifyBodyLocationField';
import TaskSelector from './selectors/taskSelector';
import WeightRestrictionMaxRangeSelector from './selectors/weightRestrictionMaxRangeSelector';
import WeightRestrictionChildValueSelectors from './selectors/weightRestrictionChildValueSelectors';
import TimeRestrictionField from './selectors/timeRestrictionField';
import YesNoRestrictionField from './selectors/yesNoRestrictionField';

/**
 * Update Redux selected tasks when the array of locally selected tasks updates.
 */
const RestrictionList = () => {
  const { restrictionListItems } = useAppSelector((state) => state.newJmrPage);

  return (
    <Column spacing="300" spacingInset="300">
      {restrictionListItems.map((_, index) => (
        <RestrictionItemBox key={index} listIndex={index} />
      ))}
      <Box spacingInset="300" />
    </Column>
  );
};

/**
 * Update the "selected task name list" when the selected name changes.
 */
const RestrictionItemBox = (props: { listIndex: number } & BoxProps) => {
  const { listIndex, ...otherProps } = props;
  const dispatch = useDispatch();
  const { restrictionListItems, isRestrictionsPageReadOnly } = useAppSelector((state) => state.newJmrPage);
  const task = restrictionListItems[listIndex];
  const displayTimeRestrictionTask = isTimeRestrictionTask(task);
  const displayParentWeightRestrictionFields = isWeightRestrictionParentTask(task);
  const displayYesNoTask = isYesNoRestrictionTask(task);

  return (
    <Card type="outline" disabled={true} {...otherProps}>
      {/* Top Row */}
      <Row alignmentHorizontal="justify">
        {/* Index */}
        <Text type="h200" color="secondary">
          Restriction {listIndex + 1}
        </Text>

        {/* Remove Button */}
        {!isRestrictionsPageReadOnly && (
          <Button
            type="link"
            onClick={() => dispatch(deleteRestrictionListItem(listIndex))}
            data-cy={`new-jmr__remove-restriction__btn__${listIndex}`}
            data-testid={`new-jmr__remove-restriction__btn__${listIndex}`}
          >
            Remove
          </Button>
        )}
      </Row>
      <Box spacingInset="200" />

      {/* Select Task */}
      <Box spacingInset="none none 400 none">
        <Text>Task*</Text>
        <TaskSelector listIndex={listIndex} />
      </Box>
      {/* <Box spacingInset="300" /> */}

      {/* Display range selector for a "Parent Weight Restriction Task" */}
      {displayParentWeightRestrictionFields && (
        <WeightRestrictionMaxRangeSelector listIndex={listIndex} spacingInset="none none 400 none" />
      )}

      {/* "Identify Body Location" Field */}
      {!!task && <IdentifyBodyLocationField listIndex={listIndex} spacingInset="none none 400 none" />}

      {/* Handle Time Restrictions*/}
      {displayParentWeightRestrictionFields && task.weightRestrictionMaxRange > 0 ? (
        <WeightRestrictionChildValueSelectors listIndex={listIndex} />
      ) : displayTimeRestrictionTask ? (
        <TimeRestrictionField listIndex={listIndex} />
      ) : displayYesNoTask ? (
        <YesNoRestrictionField listIndex={listIndex} />
      ) : null}
    </Card>
  );
};

// **** Export default **** //

export default RestrictionList;
